import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialOrderWrapper } from '../Styled';
import { BackButtonRow } from '../../../shared/components/BackButton/Styled';
import { IconButton } from '@mui/material';
import BackIcon from '../../../shared/svg/BackIcon';
import {
    BackButtonStyle,
    ButtonRow,
    StyledButton,
    TitleText,
    TitleWrapper,
} from '../../../shared/styled/TitleBar';
import {
    CreateMaterialOrderTitleBar,
    WarningDiscardMaterialProductOrder,
} from '../../../configs/materialOrder';
import { SingleMaterialOrder } from '../single';
import SelectProviders from './SelectProviders';
import useMergeState from '../../../shared/hooks/useMergeState';
import { api } from '../../../shared/hooks/api';
import { error, success } from '../../../utils/toast';
import CancelModal from '../../../shared/components/CancelModal';
import { WarningUnsavedChanges } from '../../../configs/warningProductModal';
import apiMethods from '../../../utils/api';
import { Material, Provider } from '../../../data/entities';
import { sum } from 'lodash';

const initialMaterialOrder = {
    due_date: new Date().toISOString().split('T')[0],
    is_urgent: false,
    provider: null,
    provider_id: null,
    provider_name: "",
    materials: [],
};
const MATERIAL_ORDER_TAB_URL = "/material-orders?tab=material%20orders";
const PROVIDER_SELECTION_STEP = "__provider_selection__";

export const CreateMaterialOrder = () => {
    const [materialOrder, mergeMaterialOrder] =
        useMergeState(initialMaterialOrder);
    const [{ isCreating }, createMaterialOrder] =
        api.post('/materials/orders/');
    const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [sourceMaterials, setSourceMaterials] = useState<Material[]>([]);
    const [isFetchingProvider, setIsFetchingProvider] = useState(false);

    // Get product order query param
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const productOrderId = queryParams.get('productOrderId');
    const storage = queryParams.get('storage');
    const [productOrder, setProductOrder] = useState();

    const { state } = useLocation() as { state?: { selectedMaterials?: Material[] } };

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (productOrderId) {
                    const [missingMaterials, fetchedProductOrder] =
                        (await Promise.all([
                            apiMethods.get(
                                `/products/orders/${productOrderId}/missing-materials?storage=${storage}`
                            ),
                            apiMethods.get(
                                `/products/orders/${productOrderId}/`
                            ),
                        ])) as [Material[], any];

                    setSourceMaterials(missingMaterials as Material[]);
                    setProductOrder(fetchedProductOrder as any);
                }


                const selectedMaterials = state?.selectedMaterials || [];

                if (!productOrderId && selectedMaterials.length > 0) {
                    setSourceMaterials(selectedMaterials);
                }

            } catch (err) {
                error(
                    'Truy cập vật tư bị thiếu thất bại, xin vui lòng nhập tay',
                    err
                );
            }
        };

        fetchData();
    }, [productOrderId]);

    // Mark as modified when materialOrder changes
    useEffect(() => {
        if (materialOrder.materials.length > 0 || materialOrder.is_urgent) {
            setIsModified(true);
        }
    }, [materialOrder]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const navigate = useNavigate();

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }
        
        navigate(path);
        return true;
    }, [isModified, navigate]);

    const hasSelectedProvider = !!materialOrder.provider_id;
    const hasOrderEdits = materialOrder.materials.length > 0 || materialOrder.is_urgent || !!materialOrder.note;

    const resetProviderSelection = () => {
        setIsModified(false);
        mergeMaterialOrder({
            provider: null,
            provider_id: null,
            provider_name: "",
            materials: [],
        });
    };

    const confirmProviderSelection = () => {
        if (hasOrderEdits) {
            setNavigationPath(PROVIDER_SELECTION_STEP);
            setShowUnsavedDialog(true);
            return false;
        }

        resetProviderSelection();
        return true;
    };

    const onClickBack = () => {
        if (hasSelectedProvider) {
            confirmProviderSelection();
            return;
        }

        productOrderId
            ? navigate('/product-orders/')
            : navigate(MATERIAL_ORDER_TAB_URL);
    };

    const onClickCancel = () => {
        productOrderId 
            ? confirmNavigation('/product-orders/')
            : confirmNavigation(MATERIAL_ORDER_TAB_URL);
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await onClickCreateMaterialOrder();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        if (navigationPath === PROVIDER_SELECTION_STEP) {
            resetProviderSelection();
            setNavigationPath("");
            return;
        }
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const onClickCreateMaterialOrder = async () => {
        if (!materialOrder.provider_id) {
            error("Lỗi tạo đơn hàng vật tư", "Vui lòng chọn nhà cung cấp");
            return false;
        }

        const materials = materialOrder.materials.map((item: any) => ({
            material_id: item.id,
            quantity: item.missing_quantity,
        }));

        const materialOrderPrice = sum(
            materialOrder.materials.map(
                (item: any) => item.provider_price * item.missing_quantity
            )
        );

        const requestBody = {
            ...materialOrder,
            provider_id: materialOrder.provider_id,
            materials,
            total_price: Math.round(materialOrderPrice),
        };

        if (productOrderId) {
            requestBody.product_order_id = productOrderId;
        }

        try {
            const resp = await createMaterialOrder(requestBody);
            success('THÀNH CÔNG', 'Tạo đơn hàng vật tư thành công');
            setIsModified(false);
            navigate(`/material-orders/${resp.id}?returnTab=material%20orders`);
            return true;
        } catch (err) {
            error('Lỗi tạo đơn hàng vật tư', err);
            return false;
        }
    };

    const handleMaterialOrderChange = (updates: any) => {
        setIsModified(true);
        mergeMaterialOrder(updates);
    };

    const onSelectProvider = async (selectedProvider: Provider) => {
        try {
            setIsFetchingProvider(true);
            const providerDetail = await apiMethods.get(`/materials/providers/${selectedProvider.id}/`) as Provider;
            mergeMaterialOrder({
                provider: providerDetail,
                provider_id: providerDetail.id,
                provider_name: providerDetail.name,
                materials: [],
            });
            setIsModified(false);
        } catch (err) {
            error("Lỗi tải thông tin nhà cung cấp", err);
        } finally {
            setIsFetchingProvider(false);
        }
    };

    return (
        <MaterialOrderWrapper>
            {isOpenCancelModal && (
                <CancelModal
                    open={isOpenCancelModal}
                    title={WarningDiscardMaterialProductOrder.title}
                    contentText={WarningDiscardMaterialProductOrder.context}
                    handleConfirm={() => onClickCancel()}
                    handleClose={() => setIsOpenCancelModal(false)}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />

            <BackButtonRow>
                <IconButton style={BackButtonStyle} onClick={onClickBack}>
                    <BackIcon />
                </IconButton>
                Trở về
            </BackButtonRow>

            <TitleWrapper>
                <TitleText>{CreateMaterialOrderTitleBar.title}</TitleText>
                {hasSelectedProvider && (
                    <ButtonRow>
                        <StyledButton
                            variant="text"
                            onClick={() => setIsOpenCancelModal(true)}
                            disabled={isCreating}
                        >
                            Huỷ bỏ
                        </StyledButton>
                        <StyledButton
                            variant="contained"
                            disabled={!materialOrder.provider_id || isCreating}
                            onClick={onClickCreateMaterialOrder}
                        >
                            Tạo đơn hàng
                        </StyledButton>
                    </ButtonRow>
                )}
            </TitleWrapper>

            {!hasSelectedProvider && (
                <SelectProviders setSelectedProvider={onSelectProvider} />
            )}

            {hasSelectedProvider && !isFetchingProvider && (
                <SingleMaterialOrder
                    materialOrder={materialOrder}
                    mergeMaterialOrder={handleMaterialOrderChange}
                    productOrder={productOrder}
                    provider={materialOrder.provider}
                    sourceMaterials={sourceMaterials}
                />
            )}
        </MaterialOrderWrapper>
    );
};
